// Module-level cache so an image is only ever fetched once per session,
// no matter how many pages/components ask for it (route preload, hover
// prefetch, and the <img> tag that eventually renders it all share this).
//
// The Image object used to trigger the fetch is kept alive in `images`
// for the lifetime of the module (never inserted into the DOM, so there's
// nothing for React — or anything else — to fight over removing). Letting
// it get garbage collected right after load would allow the browser to
// drop the decoded bitmap and re-fetch/re-decode when the real <img> or
// CSS background-image later references the same URL; holding the
// reference keeps that resource "hot" so every later consumer of the URL
// resolves against the same load.
const images = new Map<string, HTMLImageElement>();
const pending = new Map<string, Promise<void>>();

function loadOne(url: string): Promise<void> {
  if (images.has(url)) return Promise.resolve();

  const inFlight = pending.get(url);
  if (inFlight) return inFlight;

  const promise = new Promise<void>((resolve) => {
    const img = new Image();

    const settle = () => {
      images.set(url, img);
      pending.delete(url);
      resolve();
    };

    // Resolve on error too — a broken asset must never block the page gate.
    img.onload = settle;
    img.onerror = settle;
    img.src = url;
  });

  pending.set(url, promise);
  return promise;
}

export function isImageCached(url: string): boolean {
  return images.has(url);
}

export function preloadImages(urls: string[]): Promise<void> {
  const unique = Array.from(new Set(urls.filter(Boolean)));
  return Promise.all(unique.map(loadOne)).then(() => undefined);
}

export function preloadImage(url: string): Promise<void> {
  return loadOne(url);
}
